import { useCallback, useEffect, useState } from 'react';
import RefreshIcon from '@mui/icons-material/Refresh';
import QrCodeIcon from '@mui/icons-material/QrCode';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import GpsNotFixedIcon from '@mui/icons-material/GpsNotFixed';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Grid,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';

import { supabase } from '../providers/supabase/supabase';

type StickerScan = {
    id: number;
    created_at: string;
    scan_number: number;
    ip: string | null;
    utm_source: string | null;
    city: string | null;
    region: string | null;
    country: string | null;
    lat: number | null;
    lon: number | null;
    precise: boolean;
};

export const StickerScansDashboard = () => {
    const [scans, setScans] = useState<StickerScan[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [mapReady, setMapReady] = useState(false);

    const fetchScans = useCallback(async () => {
        setLoading(true);
        setError(null);
        const { data, error: fetchError } = await supabase
            .from('sticker_scans')
            .select('*')
            .order('created_at', { ascending: false });

        if (fetchError) {
            setError(fetchError.message);
        } else {
            setScans(data || []);
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        fetchScans();
    }, [fetchScans]);

    // Load Leaflet CSS dynamically
    useEffect(() => {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        document.head.appendChild(link);

        const script = document.createElement('script');
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.onload = () => setMapReady(true);
        document.head.appendChild(script);

        return () => {
            document.head.removeChild(link);
            document.head.removeChild(script);
        };
    }, []);

    // Render map when data + leaflet are ready
    useEffect(() => {
        if (!mapReady || scans.length === 0) return;

        const L = (window as any).L;
        if (!L) return;

        const container = document.getElementById('sticker-map');
        if (!container) return;

        // Clear previous map instance
        if ((container as any)._leaflet_id) {
            (container as any)._leaflet_id = null;
            container.innerHTML = '';
        }

        const geoScans = scans.filter(s => s.lat && s.lon);
        const map = L.map('sticker-map').setView([40.72, -73.99], 12);

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; OpenStreetMap',
        }).addTo(map);

        // Group nearby scans
        const groups: Record<string, { lat: number; lon: number; scans: StickerScan[] }> = {};
        geoScans.forEach(s => {
            const precision = s.precise ? 1000 : 100;
            const key = `${Math.round(s.lat! * precision) / precision},${Math.round(s.lon! * precision) / precision}`;
            if (!groups[key]) groups[key] = { lat: s.lat!, lon: s.lon!, scans: [] };
            groups[key].scans.push(s);
        });

        const markers: [number, number][] = [];
        Object.values(groups).forEach(g => {
            const count = g.scans.length;
            const radius = Math.min(6 + count * 3, 30);
            const hasPrecise = g.scans.some(s => s.precise);
            const latest = g.scans[0];

            const circle = L.circleMarker([g.lat, g.lon], {
                radius,
                fillColor: hasPrecise ? '#e74c3c' : '#f39c12',
                color: hasPrecise ? '#c0392b' : '#e67e22',
                weight: 2,
                fillOpacity: 0.7,
            }).addTo(map);

            circle.bindPopup(
                `<strong>${latest.city || 'Unknown'}${latest.region ? ', ' + latest.region : ''}</strong><br/>` +
                `${count} scan${count > 1 ? 's' : ''} ${hasPrecise ? '(GPS)' : '(approx)'}<br/>` +
                `Latest: ${new Date(latest.created_at).toLocaleString()}`
            );

            markers.push([g.lat, g.lon]);
        });

        if (markers.length > 0) {
            map.fitBounds(markers, { padding: [40, 40] });
        }
    }, [mapReady, scans]);

    const totalScans = scans.length;
    const preciseScans = scans.filter(s => s.precise).length;
    const uniqueCities = new Set(scans.map(s => s.city).filter(Boolean)).size;
    const todayScans = scans.filter(s => {
        const scanDate = new Date(s.created_at).toDateString();
        return scanDate === new Date().toDateString();
    }).length;

    return (
        <Stack spacing={3} sx={{ py: 4 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box display="flex" alignItems="center" gap={1.5}>
                    <QrCodeIcon sx={{ fontSize: 32 }} />
                    <Box>
                        <Typography variant="h4" component="h1">
                            Sticker Scans
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            QR code scan tracking with geolocation
                        </Typography>
                    </Box>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<RefreshIcon />}
                    onClick={fetchScans}
                    disabled={loading}
                >
                    Refresh
                </Button>
            </Box>

            {error && <Alert severity="error">{error}</Alert>}

            {loading && scans.length === 0 ? (
                <Box display="flex" justifyContent="center" py={8}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    {/* Stats cards */}
                    <Grid container spacing={3}>
                        <Grid item xs={6} sm={3}>
                            <Card>
                                <CardContent>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Total Scans
                                    </Typography>
                                    <Typography variant="h4">{totalScans}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Card>
                                <CardContent>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Today
                                    </Typography>
                                    <Typography variant="h4">{todayScans}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Card>
                                <CardContent>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        GPS Precise
                                    </Typography>
                                    <Typography variant="h4">{preciseScans}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Card>
                                <CardContent>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Unique Cities
                                    </Typography>
                                    <Typography variant="h4">{uniqueCities}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Map */}
                    <Card>
                        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    px: 2,
                                    pt: 2,
                                    pb: 1,
                                }}
                            >
                                <Typography variant="h6">Scan Map</Typography>
                                <Chip
                                    size="small"
                                    icon={<GpsFixedIcon />}
                                    label="GPS"
                                    sx={{ bgcolor: '#fdecea', color: '#c0392b' }}
                                />
                                <Chip
                                    size="small"
                                    icon={<GpsNotFixedIcon />}
                                    label="Approx"
                                    sx={{ bgcolor: '#fef3e2', color: '#e67e22' }}
                                />
                            </Box>
                            <div
                                id="sticker-map"
                                style={{ height: 500, width: '100%' }}
                            />
                        </CardContent>
                    </Card>

                    {/* Recent scans table */}
                    <Card>
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 2 }}>
                                Recent Scans
                            </Typography>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>#</TableCell>
                                            <TableCell>Time</TableCell>
                                            <TableCell>Location</TableCell>
                                            <TableCell>Source</TableCell>
                                            <TableCell>Precision</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {scans.slice(0, 50).map(scan => (
                                            <TableRow key={scan.id}>
                                                <TableCell>{scan.scan_number}</TableCell>
                                                <TableCell>
                                                    {new Date(scan.created_at).toLocaleString()}
                                                </TableCell>
                                                <TableCell>
                                                    {[scan.city, scan.region, scan.country]
                                                        .filter(Boolean)
                                                        .join(', ') || 'Unknown'}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={scan.utm_source || 'direct'}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {scan.precise ? (
                                                        <Chip
                                                            icon={<GpsFixedIcon />}
                                                            label="GPS"
                                                            size="small"
                                                            color="error"
                                                            variant="outlined"
                                                        />
                                                    ) : (
                                                        <Chip
                                                            icon={<GpsNotFixedIcon />}
                                                            label="IP"
                                                            size="small"
                                                            color="warning"
                                                            variant="outlined"
                                                        />
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                </>
            )}
        </Stack>
    );
};
